/**
 * Draft take-off structures — created during PDF take-off before pushing to proposal.
 */

import { db } from './firebase';
import {
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PdfTakeoffStructure } from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const COLLECTION = 'pdfTakeoffStructures';

function structureFromDoc(snap: { id: string; data: () => Record<string, unknown> }): PdfTakeoffStructure {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const updated = d.updatedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    dealId: d.dealId as string,
    leadId: d.leadId as string,
    name: (d.name as string) || 'Structure',
    userId: d.userId as string,
    userName: (d.userName as string) || 'Unknown',
    createdAt: created?.toDate?.() ?? new Date(),
    updatedAt: updated?.toDate?.() ?? undefined,
  };
}

export function subscribeDealTakeoffStructures(
  dealId: string,
  callback: (list: PdfTakeoffStructure[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('dealId', '==', dealId));
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => structureFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** Doc id matches future ProposalNodeSet.id for seamless push. */
export async function createTakeoffStructure(
  input: Omit<PdfTakeoffStructure, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const structureId = `nodeset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const payload = removeUndefinedDeep({
    ...input,
    createdAt: Timestamp.now(),
  });
  await setDoc(doc(db, COLLECTION, structureId), payload);
  return structureId;
}

export async function updateTakeoffStructureName(id: string, name: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    name: name.trim(),
    updatedAt: Timestamp.now(),
  });
}

export async function deleteTakeoffStructure(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
